import { useMemo } from "react";
import { useAuth } from "@clerk/nextjs";
import { useUserPreferences } from "modules/auth/hooks/useUserPreferences";
import { type GameCapability, type GameId } from "modules/common/model/models";
import { useGamesForCapability } from "modules/common/gameRegistryContext";

/**
 * Hook to determine the default game based on user preferences.
 * If the user has only one game selected in their preferences,
 * that game becomes the default and the selector can be hidden.
 *
 * @returns Object containing:
 *   - defaultGame: The single game if only one is selected, otherwise null
 *   - hasSingleGame: Boolean indicating if user has exactly one game
 *   - shouldShowSelector: Convenience boolean (opposite of hasSingleGame)
 *   - isLoading: Whether preferences are still loading
 */
export function useDefaultGame(capability: GameCapability = "catalogue") {
  const { userId } = useAuth();
  const {
    preferences,
    isLoading: queryLoading,
    isFetched,
  } = useUserPreferences();
  const { games: supportedGames } = useGamesForCapability(capability);
  const isLoading = queryLoading || (!!userId && !isFetched);

  return useMemo(() => {
    const preferredGames = preferences?.games ?? [];
    const games =
      !userId || preferredGames.length === 0
        ? supportedGames
        : preferredGames.filter((game) => supportedGames.includes(game));
    const hasSingleGame = games.length === 1;
    const defaultGame: GameId | null = hasSingleGame ? games[0] : null;
    const gamesFilter: GameId[] | undefined =
      games.length > 0 && games.length < supportedGames.length
        ? games
        : undefined;

    return {
      games,
      supportedGames,
      gamesFilter,
      defaultGame,
      hasSingleGame,
      shouldShowSelector: !hasSingleGame,
      isLoading,
    };
  }, [preferences?.games, supportedGames, isLoading, userId]);
}
